import { useMemo } from 'react';
import { RotateCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DEFAULT_PALETTE,
  type ColorPalette,
  type QuestionnaireAnswers,
} from './questions';

interface LivePreviewProps {
  answers: QuestionnaireAnswers;
  /** Optional manual reload — bumping this re-runs the layout effect. */
  onReload?: () => void;
}

/**
 * The live style-preview panel.
 *
 * Renders a canonical 3-section website mock (header / hero / feature trio)
 * re-themed by the user's current answers. Exists to give the user
 * confidence that their typography, palette, border style, layout density,
 * and brand tone choices are doing something — without growing a real
 * site stub from incomplete answers.
 *
 * Pure render — no state of its own. Dependencies on `answers` are
 * surfaced through useMemo for the derived design-tokens object.
 */
export function LivePreview({ answers, onReload }: LivePreviewProps) {
  const tokens = useMemo(() => deriveTokens(answers), [answers]);

  return (
    <div className="sticky top-6 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="smallcaps text-graphite">Live preview</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReload}
          className="h-7 px-2 font-mono text-[11px]"
          aria-label="Reload preview"
        >
          <RotateCw className="h-3 w-3" />
          Reload
        </Button>
      </div>

      <div
        className="overflow-hidden border border-ink/15 shadow-[0_8px_24px_-12px_rgba(20,19,15,0.18)]"
        style={{
          // The "browser chrome" stays AURA-themed; only the inner stage
          // adopts the user's tokens.
          borderRadius: 4,
        }}
      >
        {/* Faux browser chrome */}
        <div className="flex items-center gap-1.5 border-b border-ink/10 bg-paper-2 px-3 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
          <span className="ml-3 truncate font-mono text-[10px] text-graphite">
            preview.aura.dev
          </span>
        </div>

        {/* Themed stage */}
        <PreviewStage tokens={tokens} answers={answers} />
      </div>

      <p className="font-mono text-[11px] leading-relaxed text-graphite">
        <span className="text-ochre">¶</span>&nbsp;&nbsp;A canonical template re-themed by your selections. This is not your actual site — it is here to show how your fonts, colours, and shapes work together.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Token derivation
// ─────────────────────────────────────────────────────────────────────────

interface PreviewTokens {
  palette: ColorPalette;
  fontStack: string;
  headingStack: string;
  headingItalic: boolean;
  radius: number;
  borderStyle: string;
  /** Corner geometry. 'beveled' is rendered with clip-path polygons because
   *  CSS has no native bevel primitive. */
  cornerTreatment: 'squared' | 'rounded' | 'beveled';
  density: 'minimal' | 'balanced' | 'maximal';
  toneCopy: { hero: string; sub: string; cta: string };
  navLinkCount: number;
}

// 6px corner cut on each side. Small enough to read at the preview's font
// size yet large enough to be unmistakably a bevel rather than rounding.
const BEVEL_CLIP_PATH =
  'polygon(6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px), 0 6px)';

function deriveTokens(a: QuestionnaireAnswers): PreviewTokens {
  // Palette — fall back to AURA editorial defaults.
  const palette: ColorPalette = a.palette ?? DEFAULT_PALETTE;

  // Typography — map family → CSS stack.
  let fontStack = 'Geist, ui-sans-serif, system-ui, sans-serif';
  let headingStack = '"Instrument Serif", Georgia, serif';
  let headingItalic = true;
  switch (a.typography_family) {
    case 'sans-serif':
      fontStack = 'Geist, ui-sans-serif, system-ui, sans-serif';
      headingStack = 'Geist, ui-sans-serif, system-ui, sans-serif';
      headingItalic = false;
      break;
    case 'serif':
      fontStack = 'Georgia, "Iowan Old Style", serif';
      headingStack = '"Playfair Display", Georgia, serif';
      headingItalic = false;
      break;
    case 'mono':
      fontStack = '"Geist Mono", ui-monospace, SFMono-Regular, monospace';
      headingStack = '"Geist Mono", ui-monospace, SFMono-Regular, monospace';
      headingItalic = false;
      break;
    case 'display':
    default:
      fontStack = 'Geist, ui-sans-serif, system-ui, sans-serif';
      headingStack = '"Instrument Serif", Georgia, serif';
      headingItalic = true;
  }

  // Border radius from shape choice. Beveled keeps radius at 0 — it's
  // rendered with `clip-path` instead, exposed via `cornerTreatment`.
  let radius = 4;
  let cornerTreatment: PreviewTokens['cornerTreatment'] = 'squared';
  if (a.border_shape === 'rounded') {
    radius = 12;
    cornerTreatment = 'rounded';
  } else if (a.border_shape === 'beveled') {
    radius = 0;
    cornerTreatment = 'beveled';
  } else if (a.border_shape === 'squared') {
    radius = 0;
    cornerTreatment = 'squared';
  }

  // 'none' is rendered by setting border-style to none AND by skipping
  // decorative borders entirely in the stage (see PreviewStage).
  let borderStyle = 'solid';
  if (a.border_line === 'dashed') borderStyle = 'dashed';
  else if (a.border_line === 'dotted') borderStyle = 'dotted';
  else if (a.border_line === 'none') borderStyle = 'none';

  // Density from layout choice.
  const density: PreviewTokens['density'] = (a.layout_style as PreviewTokens['density']) || 'balanced';

  // Copy from brand tone — small voice changes only.
  const toneCopy = ((): PreviewTokens['toneCopy'] => {
    switch (a.brand_tone) {
      case 'professional':
        return { hero: 'Built for serious work.', sub: 'A focused tool for teams that ship.', cta: 'Request a demo' };
      case 'friendly':
        return { hero: 'Hey, glad you stopped by.', sub: 'A gentler way to get things done.', cta: 'Try it out' };
      case 'playful':
        return { hero: 'Make something delightful.', sub: 'No rules, just good ideas and great vibes.', cta: 'Start playing' };
      case 'luxury':
        return { hero: 'Quietly exceptional.', sub: 'Considered design, made to last.', cta: 'Discover the collection' };
      case 'futuristic':
        return { hero: 'Tomorrow, made today.', sub: 'A platform built on what comes next.', cta: 'Enter the system' };
      default:
        return { hero: 'A clearer way to do this.', sub: 'A short subtitle that explains your value.', cta: 'Get started' };
    }
  })();

  // Nav links from question 9.
  let navLinkCount = 5;
  if (a.nav_link_count === 'small') navLinkCount = 3;
  else if (a.nav_link_count === 'medium') navLinkCount = 5;
  else if (a.nav_link_count === 'large') navLinkCount = 7;

  return {
    palette,
    fontStack,
    headingStack,
    headingItalic,
    radius,
    borderStyle,
    cornerTreatment,
    density,
    toneCopy,
    navLinkCount,
  };
}

// ─────────────────────────────────────────────────────────────────────────
//  Stage
// ─────────────────────────────────────────────────────────────────────────

function PreviewStage({
  tokens,
  answers: _answers,
}: {
  tokens: PreviewTokens;
  answers: QuestionnaireAnswers;
}) {
  const { palette, fontStack, headingStack, headingItalic, radius, borderStyle, density, toneCopy, navLinkCount } = tokens;

  // Density adjusts spacing scale.
  const space = density === 'minimal' ? 28 : density === 'maximal' ? 14 : 20;
  const featurePadding = density === 'minimal' ? 18 : density === 'maximal' ? 10 : 14;

  const navLinks = ['Home', 'Work', 'About', 'Pricing', 'Blog', 'Contact', 'More'].slice(0, navLinkCount);

  // Corner geometry. CSS has no bevel primitive, so beveled corners use
  // a clip-path polygon — the shared trim is defined at module scope.
  // Note: clip-path also clips any `border` set on the same element. That
  // is the visually correct behaviour for bevels (the border traces the
  // cut edge), and matches what FORGE will emit downstream.
  const cornerStyle: React.CSSProperties =
    tokens.cornerTreatment === 'beveled'
      ? { clipPath: BEVEL_CLIP_PATH }
      : tokens.cornerTreatment === 'rounded'
        ? { borderRadius: radius }
        : {}; // squared

  return (
    <div
      style={{
        background: palette.secondary,
        color: palette.primary,
        fontFamily: fontStack,
        padding: space,
        minHeight: 360,
      }}
      className="text-[11px] leading-tight"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between"
        style={{ borderBottom: `1px ${borderStyle} ${withAlpha(palette.primary, 0.18)}`, paddingBottom: space * 0.6 }}
      >
        <span
          style={{
            fontFamily: headingStack,
            fontStyle: headingItalic ? 'italic' : 'normal',
            fontSize: 16,
            color: palette.primary,
          }}
        >
          Studio Sample
        </span>
        <nav className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {navLinks.map((l) => (
            <span key={l} style={{ color: withAlpha(palette.primary, 0.7), fontSize: 9 }}>
              {l}
            </span>
          ))}
          <span
            style={{
              ...cornerStyle,
              background: palette.accent,
              color: bestForeground(palette.accent, palette.primary, palette.secondary),
              padding: '3px 8px',
              fontSize: 9,
              fontWeight: 500,
            }}
          >
            {toneCopy.cta}
          </span>
        </nav>
      </div>

      {/* Hero */}
      <div style={{ marginTop: space, display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: space }}>
        <div>
          <p
            style={{
              fontSize: 8,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: palette.detail,
            }}
          >
            ¶ &nbsp;new
          </p>
          <h1
            style={{
              fontFamily: headingStack,
              fontStyle: headingItalic ? 'italic' : 'normal',
              fontSize: 28,
              lineHeight: 1.05,
              marginTop: 8,
              color: palette.primary,
            }}
          >
            {toneCopy.hero}
          </h1>
          <p style={{ marginTop: 10, fontSize: 10, lineHeight: 1.5, color: withAlpha(palette.primary, 0.7) }}>
            {toneCopy.sub}
          </p>
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <span
              style={{
                ...cornerStyle,
                background: palette.primary,
                color: palette.secondary,
                padding: '6px 12px',
                fontSize: 9,
                fontWeight: 500,
              }}
            >
              {toneCopy.cta}
            </span>
            <span
              style={{
                ...cornerStyle,
                border: `1px ${borderStyle} ${withAlpha(palette.primary, 0.35)}`,
                color: palette.primary,
                padding: '6px 12px',
                fontSize: 9,
                fontWeight: 500,
              }}
            >
              Learn more
            </span>
          </div>
        </div>

        {/* Hero illustration block */}
        <div
          style={{
            ...cornerStyle,
            background: withAlpha(palette.detail, 0.18),
            border: `1px ${borderStyle} ${withAlpha(palette.primary, 0.12)}`,
            minHeight: 110,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: withAlpha(palette.primary, 0.4),
            fontSize: 9,
            fontStyle: 'italic',
          }}
        >
          hero artwork
        </div>
      </div>

      {/* Feature trio */}
      <div style={{ marginTop: space, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: featurePadding }}>
        {['Considered', 'Composable', 'Calm'].map((title, i) => (
          <div
            key={title}
            style={{
              ...cornerStyle,
              border: `1px ${borderStyle} ${withAlpha(palette.primary, 0.18)}`,
              padding: featurePadding,
              background: withAlpha(palette.secondary, 0.6),
            }}
          >
            <p
              style={{
                fontSize: 8,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                color: palette.accent,
              }}
            >
              0{i + 1}
            </p>
            <p
              style={{
                marginTop: 6,
                fontFamily: headingStack,
                fontStyle: headingItalic ? 'italic' : 'normal',
                fontSize: 13,
                color: palette.primary,
              }}
            >
              {title}
            </p>
            <p style={{ marginTop: 6, fontSize: 9, lineHeight: 1.5, color: withAlpha(palette.primary, 0.7) }}>
              A short paragraph that explains the value of this feature.
            </p>
          </div>
        ))}
      </div>

      {/* Footer rule */}
      <div
        style={{
          borderTop: `1px ${borderStyle} ${withAlpha(palette.primary, 0.18)}`,
          marginTop: space,
          paddingTop: space * 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 8,
          color: withAlpha(palette.primary, 0.5),
        }}
      >
        <span>© 2026 Studio Sample</span>
        <span>preview · live</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Tiny colour utilities — no dependency on a colour library
// ─────────────────────────────────────────────────────────────────────────

/** Mix a hex colour with an alpha channel; returns rgba(). */
function withAlpha(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  const expanded = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = parseInt(expanded, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

/** Pick whichever foreground gives more contrast against a background. */
function bestForeground(bg: string, opt1: string, opt2: string): string {
  const lum = relativeLuminance(bg);
  // Light bg → use the darker option; dark bg → lighter.
  const lum1 = relativeLuminance(opt1);
  const lum2 = relativeLuminance(opt2);
  const dark = lum1 < lum2 ? opt1 : opt2;
  const light = lum1 < lum2 ? opt2 : opt1;
  return lum > 0.5 ? dark : light;
}

function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
